/* =============================================
   BlogAdvancedFilter – Standalone custom element
   Does not modify any default theme JS.

   This component:
   1. Reads a JSON tag-map output by the section
   2. Dynamically adds data-tags to article cards
   3. Injects a "no results" message
   4. Handles all filter interactions
   ============================================= */

class BlogAdvancedFilter extends HTMLElement {
  constructor() {
    super();

    this.tagMap = this.loadTagMap();
    this.articles = this.discoverArticles();
    this.noResults = this.injectNoResults();

    this.checkboxes = this.querySelectorAll('input[type="checkbox"][data-filter]');
    this.clearBtn = this.querySelector('[data-clear-filters]');
    this.toggleBtns = this.querySelectorAll('.blog-filter-advanced__toggle','blog-filter-advanced__search-input');
    this.activeContainer = this.querySelector('[data-active-filters]');
    this.searchInput = this.querySelector('[data-ingredient-search]');
    this.tagSearchInput = this.querySelector('[data-tag-search]');
    this.alphabetSearchInput = this.querySelectorAll('alphabet-filter-button')

    this.applyTagsToArticles();
    this.bindEvents();
  }

  /* =========================================
     Initialisation helpers
     ========================================= */

  /**
   * Read the JSON tag-map that the section outputs.
   * Map format: { "/blogs/handle/article-handle": "tag1,tag2,..." }
   */
  loadTagMap() {
    const script = document.querySelector('[data-blog-tag-map]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('BlogAdvancedFilter: Could not parse tag map.', e);
      return {};
    }
  }

  /**
   * Find all article card wrappers rendered by the default main-blog section.
   */
  discoverArticles() {
    return document.querySelectorAll('.blog-articles .blog-articles__article');
  }

  /**
   * Walk through visible article cards, find their link, look up tags
   * in the map, and set data-tags on the wrapper element.
   */
  applyTagsToArticles() {
    this.articles.forEach((article) => {
      const link = article.querySelector('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (href && this.tagMap[href] !== undefined) {
        article.setAttribute('data-tags', this.tagMap[href]);
      }
    });
  }

  /**
   * Create and inject a hidden "no results" element after the articles grid.
   */
  injectNoResults() {
    const grid = document.querySelector('.blog-articles');
    if (!grid) return null;
    const el = document.createElement('div');
    const p = document.createElement('p');
    el.className = 'blog-filter-advanced__no-results';
    el.hidden = true;
    p.textContent = 'No articles match your selected filters. Try adjusting your criteria.';
    el.appendChild(p);
    grid.parentNode.insertBefore(el, grid.nextSibling);
    return el;
  }

  /* =========================================
     Event binding
     ========================================= */

  bindEvents() {
    // Toggle dropdown panels
    this.toggleBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePanel(btn);
      });
    });

    // Checkbox changes trigger filter
    this.checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => this.applyFilters());
    });

    // Clear all
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearAll());
    }

    // Click outside closes panels
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeAllPanels();
      }
    });

    // Escape key closes panels
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAllPanels();
    });

    // Ingredient search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.searchIngredients(e));
    }

    // Tag search
    if (this.tagSearchInput) {
      this.tagSearchInput.addEventListener('input', (e) => this.searchTags(e));
    }

    // if(this.alphabetSearchInput){
    //   this.alphabetSearchInput.forEach(alph=>alph.addEventListener('click', e=>this.searchAlphabet(e)))
    // }
  }

  /* =========================================
     Panel toggling
     ========================================= */

  togglePanel(btn) {
    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    this.closeAllPanels();
    if (!isOpen) {
      const panel = btn.nextElementSibling;
      btn.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
    }
  }

  closeAllPanels() {
    this.toggleBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      const panel = btn.nextElementSibling;
      if (panel) panel.hidden = true;
    });
  }

  /* =========================================
     Core filtering
     ========================================= */

  applyFilters() {
    const selected = { target: [], skin: [], tag: [], ingredient: [], alphabet:[]};

    this.checkboxes.forEach((cb) => {
      if (cb.checked) {
        selected[cb.dataset.filter].push(cb.value);
      }
    });

    const hasFilters = Object.values(selected).some((arr) => arr.length > 0);

    // Show / hide Clear All button
    if (this.clearBtn) {
      this.clearBtn.hidden = !hasFilters;
    }

    // Update per-group count badges
    this.updateCounts(selected);

    // Update active filter pills
    this.updateActivePills(selected);

    // Filter articles
    let visibleCount = 0;

    this.articles.forEach((article) => {
      // Articles without data-tags (not in our map) stay visible always
      if (!article.hasAttribute('data-tags')) {
        article.style.display = '';
        visibleCount++;
        return;
      }

      const rawTags = article.dataset.tags || '';
      const tags = rawTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      let matches = true;

      // For each group with selections:
      //   - "tag" and "ingredient" groups use OR logic (article matches if it has ANY selected value)
      //   - All other groups use AND logic (article must have ALL selected values)
      // Across groups it's AND logic
      for (const [group, values] of Object.entries(selected)) {
        if (values.length > 0) {

          let groupMatch;

          if (group === 'tag' || group === 'ingredient' || group === 'target' || group === 'skin') {
            groupMatch = values.some((v) => tags.includes(v));
          } else if (group === 'alphabet'){
            groupMatch = tags.includes(`_ingredient_${values}`);
            const alphabetCheckboxes = document.querySelectorAll('.alphabet-checkbox');
            alphabetCheckboxes.forEach(cb=>{
             if(cb.checked && cb.value !== values){
                cb.checked = false;
               }
            })
            for (let i = 0; i < tags.length; i++) {
              const tag = tags[i];
              groupMatch = tag.includes(`_ingredient_${values[0].toLowerCase()}`);
              if(groupMatch){
                break
              }
            }
          } else {
            groupMatch = values.every((v) => tags.includes(v));
          }
          // const groupMatch = (group === 'tag' || group === 'ingredient'|| group === 'target' || group === 'skin' || group === 'alphabet')
          //   ? values.some((v) => tags.includes(v))
          //   : values.every((v) => tags.includes(v));
          if (!groupMatch) {
            matches = false;
            break;
          }
        }
      }

      article.style.display = matches ? '' : 'none';
      if (matches) visibleCount++;
    });

    // Show "no results" when filters are active but nothing matches
    if (this.noResults) {
      this.noResults.hidden = visibleCount > 0 || !hasFilters;
    }
  }

  /* =========================================
     Count badges
     ========================================= */

  updateCounts(selected) {
    for (const [group, values] of Object.entries(selected)) {
      const countEl = this.querySelector(
        `[data-filter-group="${group}"] [data-count]`
      );
      if (countEl) {
        countEl.textContent = values.length;
        countEl.hidden = values.length === 0;
      }
    }
  }

  /* =========================================
     Active filter pills
     ========================================= */

  updateActivePills(selected) {
    if (!this.activeContainer) return;

    const allSelected = [];
    this.checkboxes.forEach((cb) => {
      if (cb.checked) {
        const labelSpan = cb.parentElement.querySelector('span');
        allSelected.push({
          value: cb.value,
          label: labelSpan ? labelSpan.textContent : cb.value,
          group: cb.dataset.filter,
        });
      }
    });

    if (allSelected.length === 0) {
      this.activeContainer.hidden = true;
      this.activeContainer.innerHTML = '';
      return;
    }

    this.activeContainer.hidden = false;
    this.activeContainer.innerHTML = allSelected
      .map(
        (f) =>
          `<button type="button" class="blog-filter-advanced__pill" data-remove-filter="${f.value}" aria-label="Remove filter: ${f.label}">${f.label} &times;</button>`
      )
      .join('');

    // Bind pill remove events
    this.activeContainer
      .querySelectorAll('[data-remove-filter]')
      .forEach((pill) => {
        pill.addEventListener('click', () => {
          const value = pill.dataset.removeFilter;
          const cb = this.querySelector(`input[value="${value}"]`);
          if (cb) {
            cb.checked = false;
            this.applyFilters();
          }
        });
      });
  }

  /* =========================================
     Clear all
     ========================================= */

  clearAll() {
    this.checkboxes.forEach((cb) => (cb.checked = false));
    this.applyFilters();
  }

  /* =========================================
     Ingredient search
     ========================================= */

  searchIngredients(e) {
    const query = e.target.value.toLowerCase().trim();
    const items = this.querySelectorAll('[data-ingredient-name]');
    items.forEach((item) => {
      const name = item.dataset.ingredientName || '';
      item.style.display = name.includes(query) ? '' : 'none';
    });
  }

  /* =========================================
     Tag search
     ========================================= */

  searchTags(e) {
    const query = e.target.value.toLowerCase().trim();
    const items = this.querySelectorAll('[data-tag-name]');
    items.forEach((item) => {
      const name = item.dataset.tagName || '';
      item.style.display = name.includes(query) ? '' : 'none';
    });
  }
}

customElements.define('blog-advanced-filter', BlogAdvancedFilter);
